import {useCallback, useMemo} from "react";
import {useDispatch} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {
    createDateBasedOnHHMM,
    endOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {daySlices} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {
    ReservationActions,
    type ReservationDaySlice,
    useReservationsState,
} from "../../../../../store/reservations.reducer";
import {
    FLOORPLAN_DAY_SLICES,
    FLOORPLAN_QUERY_PARAMS,
    type FloorplanDaySlice,
    floorplanDateParam,
    floorplanDateSelection,
    floorplanStorageKeys,
    isFloorplanTime,
    isValidDate,
} from "../../floorplanState";

export interface CustomTimeframe {
    start: string;
    end: string;
}

type TemporalInput = Date | string | number | null | undefined;

function startOfDay(d: Date): Date {
    const normalized = new Date(d);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
}

function mergeDateAndTime(calendarDate: Date, timeSource: Date): Date {
    const merged = new Date(calendarDate);
    merged.setHours(
        timeSource.getHours(),
        timeSource.getMinutes(),
        timeSource.getSeconds(),
        timeSource.getMilliseconds(),
    );
    return merged;
}

function getNowOnSelectedDate(selectedDate: Date): Date {
    return mergeDateAndTime(selectedDate, new Date());
}

function toDate(value: TemporalInput): Date | null {
    if (value == null) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function reservationIntersectsTimeframe(
    start: TemporalInput,
    end: TemporalInput,
    timeframeStart: Date | null,
    timeframeEnd: Date | null,
): boolean {
    const rangeStart = toDate(timeframeStart);
    const rangeEnd = toDate(timeframeEnd);
    if (!rangeStart || !rangeEnd) return true;
    const reservationStart = toDate(start);
    const reservationEnd = toDate(end);
    if (!reservationStart || !reservationEnd) return false;

    const isPointInTime = rangeStart.getTime() === rangeEnd.getTime();
    if (isPointInTime) {
        // In point-in-time mode we keep ongoing + upcoming reservations.
        return reservationEnd > rangeStart;
    }

    return reservationStart < rangeEnd && reservationEnd > rangeStart;
}

export function isReservationOngoingAt(
    start: TemporalInput,
    end: TemporalInput,
    referenceTime: Date,
): boolean {
    const reservationStart = toDate(start);
    const reservationEnd = toDate(end);
    const validReferenceTime = toDate(referenceTime);
    if (!reservationStart || !reservationEnd || !validReferenceTime) return false;
    return reservationStart <= validReferenceTime && validReferenceTime < reservationEnd;
}

export function isReservationUpcomingAt(
    start: TemporalInput,
    referenceTime: Date,
): boolean {
    const reservationStart = toDate(start);
    const validReferenceTime = toDate(referenceTime);
    if (!reservationStart || !validReferenceTime) return false;
    return reservationStart > validReferenceTime;
}

export function useTimeframe() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const appId = useCurrentAppId();
    const {
        fromDate,
        activeTab,
        reservationDaySlice,
        startTimeFrame,
        endTimeFrame,
    } = useReservationsState(appId);

    // The floorplan URL is hydrated into Redux by useFloorplanViewState.
    const requestedDate = startOfDay(fromDate);
    const date = isValidDate(requestedDate) ? requestedDate : startOfDay(new Date());
    const fallbackSlice: ReservationDaySlice = activeTab === "floorplan" ? "now" : "all_day";
    const allowed: readonly ReservationDaySlice[] = activeTab === "floorplan"
        ? FLOORPLAN_DAY_SLICES
        : ["all_day", "morning", "day", "evening", "custom"];
    const requestedSlice = reservationDaySlice ?? fallbackSlice;
    const resolvedSlice: ReservationDaySlice = allowed.includes(requestedSlice as ReservationDaySlice)
        ? requestedSlice as ReservationDaySlice
        : fallbackSlice;
    const customTimeframe = useMemo<CustomTimeframe>(() => ({
        start: isFloorplanTime(startTimeFrame) ? startTimeFrame : "00:00",
        end: isFloorplanTime(endTimeFrame) ? endTimeFrame : "23:59",
    }), [endTimeFrame, startTimeFrame]);

    const restaurantId = searchParams.get(FLOORPLAN_QUERY_PARAMS.restaurantId);
    const commitTimeframe = useCallback((
        nextSlice: FloorplanDaySlice,
        nextRange: CustomTimeframe,
        nextDate?: Date,
    ) => {
        const normalizedRange = {
            start: isFloorplanTime(nextRange.start) ? nextRange.start : customTimeframe.start,
            end: isFloorplanTime(nextRange.end) ? nextRange.end : customTimeframe.end,
        };
        const nextDateSelection = nextDate && isValidDate(nextDate)
            ? floorplanDateSelection(nextDate)
            : undefined;
        const payload: Record<string, unknown> = {
            reservationDaySlice: nextSlice,
            startTimeFrame: normalizedRange.start,
            endTimeFrame: normalizedRange.end,
        };

        if (nextDateSelection) {
            payload.fromDate = nextDateSelection.date.toISOString();
            payload.toDate = endOfDay(nextDateSelection.date).toISOString();
        }

        if (restaurantId) {
            const storageKeys = floorplanStorageKeys(restaurantId);
            localStorage.setItem(storageKeys.dayPart, nextSlice);
            localStorage.setItem(storageKeys.timeStart, normalizedRange.start);
            localStorage.setItem(storageKeys.timeEnd, normalizedRange.end);
        }

        // The day itself is persisted by useFloorplanViewState, which reacts to this param
        // and writes it to the sector-wide key the calendar reads.
        setSearchParams(currentSearchParams => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.dayPart, nextSlice);
            nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.timeStart, normalizedRange.start);
            nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.timeEnd, normalizedRange.end);
            if (nextDateSelection) {
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.date, floorplanDateParam(nextDateSelection));
            }
            return nextSearchParams;
        }, {replace: true});

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload,
        });
    }, [appId, customTimeframe, dispatch, restaurantId, setSearchParams]);

    const setReservationDaySlice = useCallback((
        nextSlice: FloorplanDaySlice,
        nextCustomTimeframe?: CustomTimeframe,
    ) => {
        commitTimeframe(nextSlice, nextCustomTimeframe ?? customTimeframe);
    }, [commitTimeframe, customTimeframe]);

    const setCustomTimeframeForDate = useCallback((
        nextDate: Date,
        nextCustomTimeframe: CustomTimeframe,
    ) => {
        commitTimeframe("custom", nextCustomTimeframe, nextDate);
    }, [commitTimeframe]);

    const {timeframeStart, timeframeEnd} = useMemo(() => {
        switch (resolvedSlice) {
            case "morning":
            case "day":
            case "evening": {
                const [startHM, endHM] = daySlices(resolvedSlice, "day");
                const start = createDateBasedOnHHMM(startHM, date);
                const end = createDateBasedOnHHMM(endHM, date);
                end.setSeconds(59, 999);
                return {
                    timeframeStart: start,
                    timeframeEnd: end,
                };
            }
            case "all_day": {
                const allDayEnd = createDateBasedOnHHMM("23:59", date);
                allDayEnd.setSeconds(59, 999);
                return {
                    timeframeStart: createDateBasedOnHHMM("00:00", date),
                    timeframeEnd: allDayEnd,
                };
            }
            case "custom": {
                const start = createDateBasedOnHHMM(customTimeframe.start, date);
                const end = createDateBasedOnHHMM(customTimeframe.end, date);
                end.setSeconds(59, 999);
                return {timeframeStart: start, timeframeEnd: end};
            }
            case "now":
            default: {
                const now = getNowOnSelectedDate(date);
                return {timeframeStart: now, timeframeEnd: new Date(now)};
            }
        }
    }, [
        date,
        resolvedSlice,
        customTimeframe,
    ]);

    const hasTimeframe = isValidDate(timeframeStart) && isValidDate(timeframeEnd);
    const isRange = hasTimeframe && timeframeStart.getTime() !== timeframeEnd.getTime();
    const isPointInTime = hasTimeframe && !isRange;
    const referenceTime = hasTimeframe ? timeframeStart : getNowOnSelectedDate(date);

    return {
        date,
        timeframeStart,
        timeframeEnd,
        referenceTime,
        hasTimeframe,
        isRange,
        isPointInTime,
        reservationDaySlice: resolvedSlice,
        customTimeframe,
        setReservationDaySlice,
        setCustomTimeframeForDate,
    };
}
